import { useNavigate, useParams } from "react-router";
import { Trash2 } from "lucide-react";
import CleButton from "@cle/components/CleButton";
import { eftHashPath } from "@cle/constants/routes";
import ConfirmationModal from "@cle/components/ConfirmationModal";
import RoundedCard from "@cle/components/RoundedCard";
import {
  useDeleteReduction,
  useReductionDocument,
  useReductionDocumentFile,
} from "@/hooks/useReductions";
import { formatDanishLongDate } from "@/util/date";
import ManualReviewInfoModal from "./components/ManualReviewInfoModal";
import SecretariatMessageCard from "./components/SecretariatMessageCard";
import UploadedDocumentPreviewCard from "./components/UploadedDocumentPreviewCard";
import { formatLessons } from "../../utils/lessonLimits";

const MISSING_VALUE = "-";

const ReductionDocumentView = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const {
    data: reduction,
    isError: isReductionError,
    isLoading: isReductionLoading,
  } = useReductionDocument(id);
  const {
    data: documentFile,
    isError: isDocumentFileError,
    isLoading: isDocumentFileLoading,
  } = useReductionDocumentFile(id);
  const deleteReductionMutation = useDeleteReduction();
  const reductionTitle = reduction?.fileName ?? "Reduktion.pdf";
  const isReported = reduction?.isReported ?? false;
  const isDeleteDisabled =
    id.length === 0 || deleteReductionMutation.isPending || isReported;

  const handleDeleteReduction = (closeConfirmation: () => void) => {
    deleteReductionMutation.mutate(id, {
      onSuccess: () => {
        closeConfirmation();
        void navigate(eftHashPath("reductions"));
      },
    });
  };

  return (
    <div className="cle-course-certificate-document">
      {isReductionError ? (
        <div className="alert alert-warning" role="alert">
          Reduktionens oplysninger kunne ikke indlæses lige nu.
        </div>
      ) : null}
      {deleteReductionMutation.isError ? (
        <div className="alert alert-warning" role="alert">
          Reduktionen kunne ikke slettes lige nu. Prøv igen.
        </div>
      ) : null}

      <div className="row g-4 align-items-start">
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <UploadedDocumentPreviewCard
              certificateTitle={reductionTitle}
              fileBlob={documentFile}
              isError={isDocumentFileError}
              isLoading={isDocumentFileLoading}
            />
          </div>
        </div>
        <div className="col-12 col-xl-6">
          <div className="d-flex flex-column gap-4">
            <RoundedCard
              className="cle-course-certificate-document__side-card"
              header="Information"
              action={
                <>
                  <ManualReviewInfoModal
                    documentType="reduktion"
                    isReported={isReported}
                  />
                  <ConfirmationModal
                    title="Slet reduktion"
                    description="Er du sikker på, at du vil slette reduktionen? Handlingen kan ikke fortrydes."
                    confirmLabel="Slet"
                    confirmVariant="destructive"
                    confirmButtonId="cle-confirm-delete-reduction"
                    cancelButtonId="cle-cancel-delete-reduction"
                    isConfirming={deleteReductionMutation.isPending}
                    isConfirmDisabled={isDeleteDisabled}
                    onConfirm={({ close }) => handleDeleteReduction(close)}
                    trigger={
                      <CleButton
                        id="cle-delete-reduction"
                        variant="destructive"
                        size="sm"
                        disabled={isDeleteDisabled}
                      >
                        <Trash2 size={16} aria-hidden strokeWidth={1.75} />
                        {deleteReductionMutation.isPending
                          ? "Sletter..."
                          : "Slet"}
                      </CleButton>
                    }
                  />
                </>
              }
            >
              <dl className="cle-course-certificate-document__info-list mb-0">
                <dt>Dokument</dt>
                <dd>{reductionTitle}</dd>

                <dt>Type</dt>
                <dd>Reduktion</dd>

                <dt>Antal lektioner</dt>
                <dd>
                  {isReductionLoading
                    ? "Indlæser"
                    : formatLessons(reduction?.numberOfLessons, MISSING_VALUE)}
                </dd>

                <dt>Uploadet</dt>
                <dd>
                  {formatDanishLongDate(reduction?.createdAt, MISSING_VALUE)}
                </dd>
              </dl>
            </RoundedCard>
            <SecretariatMessageCard subjectId={id} isReported={isReported} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReductionDocumentView;
